import React, { useState } from 'react';
import { Alert, Button, Empty } from 'antd';
import type {
  ImageDescriptor,
  ImageVariantResult,
} from '@/api/ProductImages/types';
import {
  executableVariant,
  generationOutput,
  matchingVariantGeneration,
} from '@/api/ProductImages/generationVariants';
import { useTranslate } from '@/translations';
import {
  variantImageLabel,
  variantImageMessage,
  variantPresentation,
  variantRetryLabel,
} from '@/modules/ProductImages/variantImageMessages';
import { PrivateImage } from '@/modules/ProductImages/PrivateImage';
import {
  activeGeneration,
  ImageryStatus,
} from '@/modules/ProductImages/ProductImages';
import { RetryVariantImageDialog } from '@/modules/ProductImages/RetryVariantImageDialog';

export const VariantStatuses = ({
  image,
}: {
  image?: ImageDescriptor | null;
}) => {
  const { t } = useTranslate();
  return image?.variantResults.length ? (
    <>
      {image.variantResults.map((result) => (
        <div key={result.variantKey}>
          <span>{variantImageLabel(t, result.variantKey)}: </span>
          <ImageryStatus
            status={matchingVariantGeneration(image, result)?.status}
          />
        </div>
      ))}
    </>
  ) : (
    <span className="text-secondary">{t('imagery.notAvailable')}</span>
  );
};

export const VariantImage = ({
  image,
  result,
  productId,
  productCode,
  primaryActive = false,
  thumbnail = false,
}: {
  image: ImageDescriptor;
  result: ImageVariantResult;
  productId?: number;
  productCode?: string;
  primaryActive?: boolean;
  thumbnail?: boolean;
}) => {
  const { t } = useTranslate();
  const [retrying, setRetrying] = useState(false);
  const generation = matchingVariantGeneration(image, result);
  const asset = generation ? generationOutput(generation) : null;
  const active = primaryActive || activeGeneration(generation?.status ?? '');
  const historical = result.retryUnavailableReason === 'CurrentImageChanged';
  const retryState =
    !generation ||
    ['Failed', 'Unknown', 'Stopped', 'Pending', 'Running'].includes(
      generation.status,
    );
  const showRetry =
    !!productId &&
    image.sourceKind === 'Generated' &&
    retryState &&
    !historical &&
    executableVariant(result.variantKey);
  const label = variantImageLabel(t, result.variantKey);
  const missing = t(variantPresentation(result.variantKey).missing, {
    style: label,
  });
  return (
    <div className="imagery-image-output">
      {asset ? (
        <PrivateImage
          src={
            thumbnail
              ? (asset.thumbnailUrl ?? asset.contentUrl)
              : asset.contentUrl
          }
          generationId={asset.assetId}
          thumbnail={thumbnail}
          label={label}
        />
      ) : thumbnail ? (
        <span>{missing}</span>
      ) : (
        <div className="imagery-image">
          <Empty description={missing} />
        </div>
      )}
      <ImageryStatus status={generation?.status} />
      {!thumbnail ? (
        <p className="imagery-caption">
          {t('imagery.sourceRevision')}:{' '}
          {t('imagery.revision', { revision: image.revision })}
        </p>
      ) : null}
      {!thumbnail &&
      generation &&
      (generation.errorMessage || generation.errorCode) ? (
        <Alert
          type={generation.status === 'Unknown' ? 'warning' : 'error'}
          showIcon
          message={variantImageMessage(
            t,
            generation.errorCode || generation.errorMessage,
            generation.status === 'Unknown'
              ? 'imagery.unknownWarning'
              : 'imagery.variantProcessingFailed',
          )}
        />
      ) : null}
      {showRetry ? (
        <>
          <Button
            disabled={active || !result.canRetry}
            onClick={() => setRetrying(true)}
          >
            {variantRetryLabel(t, result.variantKey)}
          </Button>
          {!result.canRetry || active ? (
            <p className="imagery-caption">
              {variantImageMessage(
                t,
                active ? 'GenerationPending' : result.retryUnavailableReason,
                'imagery.variantRetryUnavailable',
              )}
            </p>
          ) : null}
        </>
      ) : null}
      {historical && !thumbnail ? (
        <p className="imagery-caption">{t('imagery.variantSourceChanged')}</p>
      ) : null}
      {retrying && productId && !active ? (
        <RetryVariantImageDialog
          productId={productId}
          productCode={productCode ?? String(productId)}
          image={image}
          result={result}
          onClose={() => setRetrying(false)}
        />
      ) : null}
    </div>
  );
};

export const VariantImages = ({
  image,
  productId,
  productCode,
  primaryActive,
  thumbnail = false,
}: {
  image: ImageDescriptor;
  productId?: number;
  productCode?: string;
  primaryActive?: boolean;
  thumbnail?: boolean;
}) => {
  const { t } = useTranslate();
  return (
    <>
      {image.variantResults.map((result) => (
        <section
          key={`${image.assetId}:${result.variantKey}`}
          aria-label={variantImageLabel(t, result.variantKey)}
        >
          {!thumbnail ? (
            <h3>{variantImageLabel(t, result.variantKey)}</h3>
          ) : (
            <span>{variantImageLabel(t, result.variantKey)}</span>
          )}
          <VariantImage
            image={image}
            result={result}
            productId={productId}
            productCode={productCode}
            primaryActive={primaryActive}
            thumbnail={thumbnail}
          />
        </section>
      ))}
    </>
  );
};
